// Desktop: 7 slides per view, mobile: 4.5. Total slides = max_products * 2 (product + gift pairs) — always.
// Desktop: arrows + pagination always in DOM; disabled (greyed) until filled slots need horizontal scroll.
// Mobile: hide arrows/pagination; touch swipe always on.
const MM_SWIPER_BREAKPOINT = 769;
const MM_SLIDES_PER_VIEW_DESKTOP = 7;
const MM_SLIDES_PER_VIEW_MOBILE = 4.5;
const MM_SLIDE_RESET_SPEED = 300;
const MM_STICKY_COMPACT_ENTER_BUFFER = 8;
const MM_STICKY_COMPACT_EXIT_BUFFER = 96;

const MM_SLIDE_GIFT_BADGE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
  <path d="M17.1525 8.44821V16.6082C17.1525 17.7889 16.2256 18.7526 15.0899 18.7526H4.40694C3.27127 18.7526 2.34438 17.7889 2.34438 16.6082V8.44821M9.75 18.7526V4.13637M9.75 4.78101L6.23541 1.30128C5.06494 0.140269 3.0783 0.949356 3.04982 2.63002C3.04982 2.64318 3.04982 2.65633 3.04982 2.66949C3.04982 3.40293 3.37882 4.05744 3.8913 4.48171M15.362 4.65603C16.0105 4.25148 16.447 3.51147 16.447 2.6662C16.447 2.65304 16.447 2.63989 16.447 2.62673C16.4217 0.946068 14.4319 0.13698 13.2614 1.29799L9.75 4.77772M18.7468 7.03396V5.65588C18.7468 5.10662 18.3134 4.65603 17.7851 4.65603H1.71169C1.18339 4.65603 0.75 5.10662 0.75 5.65588V7.03396C0.75 7.58321 1.18339 8.0338 1.71169 8.0338H17.7883C18.3166 8.0338 18.75 7.58321 18.75 7.03396H18.7468Z" stroke="white" stroke-width="1.5" stroke-miterlimit="10"/>
</svg>`;

const MM_SLIDE_REMOVE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
  <path d="M0.800003 0.800049L10.4 10.4" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M10.4 0.800049L0.800003 10.4" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

class SelectedProductsSlider extends HTMLElement {
  connectedCallback() {
    this.summaryTitle = this.querySelector('.summary-title');
    this.summaryGifts = this.querySelector('.summary-gifts');
    this.footerRoot = this.closest('.mm-footer') || this.parentElement;
    this.discountBlocks = this.footerRoot
      ? Array.from(this.footerRoot.querySelectorAll('.summary-discount'))
      : [];
    this.swiperWrapper = this.querySelector('.swiper-wrapper');
    this.addMoreNote = this.querySelector('.add-more-note');

    this._swiper = null;
    this._mqMobile = window.matchMedia(`(max-width: ${MM_SWIPER_BREAKPOINT - 1}px)`);
    this._onMqMobile = () => this._onBreakpointChange();
    this._mqMobile.addEventListener('change', this._onMqMobile);
    this._stickyRaf = null;
    this._isFooterSticky = false;
    this._onWindowScroll = () => this._requestStickySync();
    this._onWindowResize = () => this._requestStickySync();
    window.addEventListener('scroll', this._onWindowScroll, { passive: true });
    window.addEventListener('resize', this._onWindowResize);

    this._initSwiper();

    subscribe(PUB_SUB_EVENTS.quantityUpdate, (state) => this._onUpdate(state));

    this._onUpdate(window.mmStorage.snapshot());
    this._requestStickySync();
  }

  disconnectedCallback() {
    this._mqMobile?.removeEventListener('change', this._onMqMobile);
    window.removeEventListener('scroll', this._onWindowScroll);
    window.removeEventListener('resize', this._onWindowResize);
    if (this._stickyRaf) {
      cancelAnimationFrame(this._stickyRaf);
      this._stickyRaf = null;
    }
  }

  _isMobile() {
    return this._mqMobile.matches;
  }

  _onBreakpointChange() {
    const swiperEl = this.querySelector('.js-mm-swiper');
    if (swiperEl) swiperEl.classList.toggle('mm-swiper--mobile', this._isMobile());
    this._requestStickySync();
    this._onUpdate(window.mmStorage.snapshot());
  }

  _requestStickySync() {
    if (this._stickyRaf) return;
    this._stickyRaf = requestAnimationFrame(() => {
      this._stickyRaf = null;
      this._syncFooterStickyState();
    });
  }

  _syncFooterStickyState() {
    if (!this.footerRoot) return;
    if (!this._isMobile()) {
      if (this._isFooterSticky) {
        this._isFooterSticky = false;
        this.footerRoot.classList.remove('mm-footer--is-sticky');
      }
      return;
    }

    const rect = this.footerRoot.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const buffer = this._isFooterSticky
      ? MM_STICKY_COMPACT_EXIT_BUFFER
      : MM_STICKY_COMPACT_ENTER_BUFFER;
    const overlapsViewport = rect.top < viewportHeight && rect.bottom > 0;
    const nearViewportBottom = rect.bottom >= viewportHeight - buffer;
    const shouldBeSticky = overlapsViewport && nearViewportBottom;

    if (shouldBeSticky !== this._isFooterSticky) {
      this._isFooterSticky = shouldBeSticky;
      this.footerRoot.classList.toggle('mm-footer--is-sticky', shouldBeSticky);
    }
  }

  _initSwiper() {
    const swiperEl = this.querySelector('.js-mm-swiper');
    const mobile = this._isMobile();
    swiperEl.classList.toggle('mm-swiper--mobile', mobile);

    this._swiper = new Swiper(swiperEl, {
      slidesPerView: MM_SLIDES_PER_VIEW_MOBILE,
      spaceBetween: 8,
      allowTouchMove: mobile,
      breakpoints: {
        [MM_SWIPER_BREAKPOINT]: {
          slidesPerView: MM_SLIDES_PER_VIEW_DESKTOP,
          spaceBetween: 8,
        },
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: false,
      },
    });

    this._syncSwiperNav(window.mmStorage.snapshot());
  }

  _onUpdate(state) {
    this._renderSlides(state);
    this._updateHeader(state);
    this._updateFooter(state);
    this._swiper?.update();
    this._syncSwiperNav(state);
  }

  // Filled product+gift pairs exceed one desktop "page" → enable arrows & dots.
  _desktopNavNeeded(state) {
    return state.totalQuantity * 2 > MM_SLIDES_PER_VIEW_DESKTOP;
  }

  _syncSwiperNav(state) {
    if (!this._swiper) return;

    const mobile = this._isMobile();
    const prevBtn = this.querySelector('.swiper-button-prev');
    const nextBtn = this.querySelector('.swiper-button-next');

    if (mobile) {
      this.classList.remove('mm-swiper--nav-active');
      this._swiper.allowTouchMove = true;
      this._swiper.allowSlideNext = true;
      this._swiper.allowSlidePrev = true;
      return;
    }

    const active = this._desktopNavNeeded(state);
    this.classList.toggle('mm-swiper--nav-active', active);

    this._swiper.allowTouchMove = false;

    if (!active) {
      this._swiper.allowSlideNext = true;
      this._swiper.allowSlidePrev = true;
      if (this._swiper.activeIndex !== 0) this._swiper.slideTo(0, MM_SLIDE_RESET_SPEED);
      this._swiper.allowSlideNext = false;
      this._swiper.allowSlidePrev = false;
      prevBtn?.classList.add('swiper-button-disabled');
      nextBtn?.classList.add('swiper-button-disabled');
      prevBtn?.setAttribute('disabled', '');
      nextBtn?.setAttribute('disabled', '');
      return;
    }

    this._swiper.allowSlideNext = true;
    this._swiper.allowSlidePrev = true;
    prevBtn?.removeAttribute('disabled');
    nextBtn?.removeAttribute('disabled');
    this._swiper.navigation?.update?.();
  }

  _renderSlides(state) {
    const maxProducts = window.mmStorage.config.max_products;

    const units = [];
    for (const p of state.products) {
      for (let i = 0; i < p.quantity; i++) {
        units.push(p.handle);
      }
    }

    const slides = [];

    for (let i = 0; i < units.length; i++) {
      slides.push(this._createProductSlide(units[i]));
      const gift = state.giftProducts[i];
      slides.push(gift ? this._createGiftSlide(gift) : this._createEmptySlide(true));
    }

    for (let i = units.length; i < maxProducts; i++) {
      slides.push(this._createEmptySlide(false));
      slides.push(this._createEmptySlide(true));
    }

    this.swiperWrapper.replaceChildren(...slides);
  }

  _createProductSlide(handle) {
    const info = window.mmStorage.getProductInfo(handle);
    const slide = document.createElement('div');
    slide.className = 'swiper-slide slide--product';

    const img = document.createElement('img');
    img.src = info?.image || '';
    img.alt = info?.title || '';
    img.loading = 'lazy';

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'slide__remove';
    removeBtn.setAttribute('aria-label', 'Entfernen');
    removeBtn.innerHTML = MM_SLIDE_REMOVE_SVG;
    removeBtn.addEventListener('click', () => this._removeUnit(handle));

    slide.append(removeBtn, img);
    return slide;
  }

  _createGiftSlide(gift) {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide slide--gift';

    const img = document.createElement('img');
    img.src = gift.image;
    img.alt = 'Gratis Jar';
    img.loading = 'lazy';

    const badge = document.createElement('span');
    badge.className = 'slide__gift-badge';
    badge.setAttribute('aria-hidden', 'true');
    badge.innerHTML = MM_SLIDE_GIFT_BADGE_SVG;

    slide.append(badge, img);

    return slide;
  }

  _createEmptySlide(isGift) {
    const slide = document.createElement('div');
    slide.className = `swiper-slide slide--placeholder${isGift ? ' slide--placeholder-gift' : ''}`;
    slide.setAttribute('aria-hidden', 'true');
    return slide;
  }

  _removeUnit(handle) {
    const storage = window.mmStorage;
    const product = storage.products.find(p => p.handle === handle);
    if (!product) return;
    storage.setProductQuantity(handle, product.quantity - 1);
  }

  _updateHeader(state) {
    const locale = window.mmConfig.locale;

    this.summaryTitle.textContent = locale.summary_title_html.replace('{count}', state.totalQuantity);
    this.summaryGifts.textContent = locale.summary_title_gift_html.replace('{count}', state.giftProducts.length);

    const setDiscountVisibility = (isVisible) => {
      this.discountBlocks.forEach((block) => {
        const badge = block.querySelector('.discount-badge');
        const youSave = block.querySelector('.you-save');
        if (!badge || !youSave) return;
        badge.classList.toggle('hidden', !isVisible);
        youSave.classList.toggle('hidden', !isVisible);
      });
    };

    if (state.discountPercentage > 0) {
      this.discountBlocks.forEach((block) => {
        const badge = block.querySelector('.discount-badge');
        const youSaveAmount = block.querySelector('.you-save__amount');
        if (badge) {
          badge.textContent = locale.discount_badge_html.replace('{discount}', state.discountPercentage);
        }
        if (youSaveAmount) youSaveAmount.textContent = _formatPrice(state.totalDiscount);
      });
      setDiscountVisibility(true);
    } else {
      setDiscountVisibility(false);
    }
  }

  _updateFooter(state) {
    const config = window.mmStorage.config;
    const locale = window.mmConfig.locale;
    const qty = state.totalQuantity;

    if (qty < config.tier_1_discount_products_qty) {
      const needed = config.tier_1_discount_products_qty - qty;
      this.addMoreNote.innerHTML = locale.add_products_for_tier_html
        .replace('{count}', needed)
        .replace('{discount}', config.tier_1_discount_value);
    } else if (qty < config.tier_2_discount_products_qty) {
      this.addMoreNote.innerHTML = locale.add_more_for_tier_html
        .replace('{discount}', config.tier_2_discount_value);
    } else if (qty < config.max_products) {
      this.addMoreNote.innerHTML = locale.add_more_products_html
        .replace('{max}', config.max_products);
    }
  }
}

if (!customElements.get('selected-products-slider')) {
  customElements.define('selected-products-slider', SelectedProductsSlider);
}
